import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { listPages, deletePage, createPage } from "@/lib/adminCmsApi";
import AdminShell from "@/components/admin/AdminShell";

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newTitle, setNewTitle] = useState("");

  const load = async () => {
    setLoading(true);
    try { setPages(await listPages()); }
    catch (e) { toast.error("Failed to load pages"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!newSlug.trim() || !newTitle.trim()) return;
    try {
      const page = await createPage({
        slug: newSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-"),
        title: newTitle.trim(),
        sections: [], is_homepage: false, status: "published",
      });
      toast.success(`Created /${page.slug}`);
      setCreating(false); setNewSlug(""); setNewTitle("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete page "${p.title}"? Cannot be undone.`)) return;
    try { await deletePage(p.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <AdminShell title="Pages">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-ags-text-muted">Manage all dynamic site pages. Click a page to edit its sections.</p>
        <button
          onClick={() => setCreating(true)}
          data-testid="new-page-btn"
          className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] font-medium transition-colors"
        ><Plus size={14} /> New Page</button>
      </div>

      {creating && (
        <form onSubmit={onCreate} className="bg-white border border-[#0E2A57]/15 p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <input type="text" required placeholder="Page Title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                 data-testid="new-page-title" className="bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none" />
          <input type="text" required placeholder="page-slug (URL)" value={newSlug} onChange={(e) => setNewSlug(e.target.value)}
                 data-testid="new-page-slug" className="bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none font-mono text-sm" />
          <div className="flex gap-2">
            <button type="submit" className="flex-1 bg-[#0E2A57] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em]">Create</button>
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border border-[#0E2A57]/30 font-mono text-[10px] uppercase tracking-[0.22em]">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-[#0E2A57]/15">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-ags-text-muted"><Loader2 size={20} className="animate-spin mr-3" /> Loading…</div>
        ) : pages.length === 0 ? (
          <div className="p-12 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-ags-text-muted">No pages yet</div>
        ) : (
          <ul className="divide-y divide-ags-hairline">
            {pages.map((p) => (
              <li key={p.id} data-testid={`page-row-${p.slug}`} className="px-6 py-5 flex items-center gap-4 hover:bg-ags-cream">
                {p.is_homepage && <Star size={14} className="text-[#2BB6E5]" />}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-[15px] text-ags-ink tracking-[-0.02em]">{p.title}</div>
                  <div className="font-mono text-xs text-ags-text-muted mt-1">/{p.slug} · {p.sections?.length || 0} sections · {p.status}</div>
                </div>
                <a href={p.is_homepage ? "/" : `/${p.slug}`} target="_blank" rel="noopener noreferrer"
                   className="text-ags-text-muted hover:text-[#0E2A57] p-2" title="View public">
                  <ExternalLink size={16} />
                </a>
                <Link to={`/admin/pages/${p.id}`} data-testid={`edit-page-${p.slug}`}
                      className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em]">
                  <Pencil size={12} /> Edit
                </Link>
                {!p.is_homepage && (
                  <button onClick={() => onDelete(p)} className="text-red-600 hover:bg-red-50 p-2" title="Delete">
                    <Trash2 size={16} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminShell>
  );
}
